import type {
  Member,
  CurrentRyderCupTeamMember,
  TeamCompetitorMemberSummary,
  TeamCompetition,
} from '../types/api.types';
import type { MemberRyderCupStats } from './ryder-cup-member-stats-container';

/**
 * Type for member points data by team
 */
export interface MemberPointsData {
  totalPoints: number;
  totalAvailable: number;
  byTeam: Map<string, { points: number; available: number }>;
}

/**
 * Builds a map of member points across all competitions
 */
export function buildMemberPointsMap(
  competitionResults: Array<{ competitionId: string; results: TeamCompetitorMemberSummary[] }>
): Map<string, MemberPointsData> {
  const pointsMap = new Map<string, MemberPointsData>();

  competitionResults.forEach(({ results }) => {
    results.forEach((result: TeamCompetitorMemberSummary) => {
      if (!pointsMap.has(result.memberID)) {
        pointsMap.set(result.memberID, {
          totalPoints: 0,
          totalAvailable: 0,
          byTeam: new Map(),
        });
      }

      const memberData = pointsMap.get(result.memberID)!;
      memberData.totalPoints += result.pointsScored;
      memberData.totalAvailable += result.availablePoints;

      if (!memberData.byTeam.has(result.team)) {
        memberData.byTeam.set(result.team, { points: 0, available: 0 });
      }

      const teamData = memberData.byTeam.get(result.team)!;
      teamData.points += result.pointsScored;
      teamData.available += result.availablePoints;
    });
  });

  return pointsMap;
}

/**
 * Gets member IDs who competed in the last N competitions
 */
export function getMembersInRecentCompetitions(sortedCompetitions: TeamCompetition[], count: number): Set<string> {
  const memberIds = new Set<string>();

  sortedCompetitions.slice(0, count).forEach((comp) => {
    comp.competitors?.forEach((competitor) => {
      memberIds.add(competitor.memberID);
    });
  });

  return memberIds;
}

/**
 * Gets team data for USA
 */
function getUsaTeamData(memberPoints: MemberPointsData | undefined) {
  return memberPoints?.byTeam.get('USA');
}

/**
 * Gets team data for Europe (checks both EUR and EU codes)
 */
function getEuropeTeamData(memberPoints: MemberPointsData | undefined) {
  return memberPoints?.byTeam.get('EUR') || memberPoints?.byTeam.get('EU');
}

/**
 * Creates team-specific stats (points and available)
 */
function createTeamStats(teamData: { points: number; available: number } | undefined) {
  return {
    pointsScored: teamData?.points || 0,
    availablePoints: teamData?.available || 0,
  };
}

/**
 * Initializes stats object for a member
 */
export function initializeMemberStats(
  member: Member,
  teamInfo: CurrentRyderCupTeamMember | undefined,
  isActiveMember: boolean,
  memberPoints: MemberPointsData | undefined
): MemberRyderCupStats {
  const usaStats = createTeamStats(getUsaTeamData(memberPoints));
  const europeStats = createTeamStats(getEuropeTeamData(memberPoints));

  return {
    member,
    teamInfo,
    totalPointsScored: memberPoints?.totalPoints || 0,
    totalAvailablePoints: memberPoints?.totalAvailable || 0,
    pointsPercentage: 0,
    usaAppearances: 0,
    usaWins: 0,
    usaPointsScored: usaStats.pointsScored,
    usaAvailablePoints: usaStats.availablePoints,
    europeAppearances: 0,
    europeWins: 0,
    europePointsScored: europeStats.pointsScored,
    europeAvailablePoints: europeStats.availablePoints,
    isActiveMember,
  };
}

/**
 * Checks if a team code represents Europe
 */
function isEuropeTeam(team: string): boolean {
  return team === 'EUR' || team === 'EU';
}

/**
 * Updates appearances and wins for a member based on competition results
 */
export function updateMemberAppearances(
  stats: MemberRyderCupStats,
  competitorTeam: string,
  competitionWinner: string | null | undefined
): void {
  if (competitorTeam === 'USA') {
    stats.usaAppearances++;
    if (competitionWinner === 'USA') {
      stats.usaWins++;
    }
  } else if (isEuropeTeam(competitorTeam)) {
    stats.europeAppearances++;
    if (competitionWinner && isEuropeTeam(competitionWinner)) {
      stats.europeWins++;
    }
  }
}

/**
 * Calculates points percentage for all stats
 */
export function calculatePointsPercentages(statsMap: Map<string, MemberRyderCupStats>): void {
  statsMap.forEach((stats) => {
    if (stats.totalAvailablePoints > 0) {
      stats.pointsPercentage = (stats.totalPointsScored / stats.totalAvailablePoints) * 100;
    }
  });
}
